/**
 * Strips a URL down to what telemetry may see: every query parameter except
 * the tracking allowlist is removed, and the fragment is dropped.
 *
 * Applied in every consent mode (CONSENT.md, "Always on, every mode"), because
 * portal URLs carry credentials and patient-linked ids (`?token=` on the magic
 * link, deposit and Medicare assignment routes, Stripe client secrets on a
 * payment return, appointment ids), and a URL is what RUM records on every
 * view, resource, error and action. The same allowlist `visitor.js` and the
 * Cloudflare worker use, so all three agree on what a "tracking parameter" is.
 *
 * An allowlist rather than the denylist `trackingParams.ts` uses for the
 * dataLayer: here nothing needs arbitrary third-party ids, and a denylist can
 * only ever be as good as the last key someone remembered.
 *
 * Dependency-free so the Datadog boot path can import it.
 */

/** Exact keys kept, compared case-insensitively. `utm_*` is kept by prefix. */
export const TRACKING_PARAM_ALLOWLIST: readonly string[] = [
  "gclid",
  "gbraid",
  "wbraid",
  "dclid",
  "fbclid",
  "msclkid",
  "ttclid",
  "li_fat_id",
];

const ALLOWLIST = new Set(TRACKING_PARAM_ALLOWLIST);

/** Whether a query parameter may survive scrubbing. */
export const isAllowedTrackingParam = (key: string): boolean => {
  const lower = key.toLowerCase();
  return lower.startsWith("utm_") || ALLOWLIST.has(lower);
};

/** Filters a query string (with or without the leading `?`). */
export const scrubSearch = (search: string): string => {
  const raw = search.startsWith("?") ? search.slice(1) : search;
  if (!raw) return "";

  // Split by hand rather than through URLSearchParams so kept values are
  // returned byte-identical (URLSearchParams re-encodes spaces and the like).
  const kept = raw.split("&").filter(pair => {
    if (!pair) return false;
    const rawKey = pair.split("=")[0] ?? "";
    let key = rawKey;
    try {
      key = decodeURIComponent(rawKey.replace(/\+/g, " "));
    } catch {
      // A malformed escape: judge the raw key, which will not be allowlisted.
    }
    return isAllowedTrackingParam(key);
  });

  return kept.length ? `?${kept.join("&")}` : "";
};

/**
 * Scrubs an absolute or relative URL. Anything that is not a string comes back
 * unchanged; a string is always returned scrubbed, even when it cannot be
 * parsed as a URL (the query and fragment are cut by hand).
 */
export function scrubUrl(url: string): string;
export function scrubUrl<T>(url: T): T;
export function scrubUrl(url: unknown): unknown {
  if (typeof url !== "string" || !url) return url;

  const hashIndex = url.indexOf("#");
  const withoutHash = hashIndex === -1 ? url : url.slice(0, hashIndex);

  const queryIndex = withoutHash.indexOf("?");
  if (queryIndex === -1) return withoutHash;

  const base = withoutHash.slice(0, queryIndex);
  const search = withoutHash.slice(queryIndex);

  return `${base}${scrubSearch(search)}`;
}
